import { Icon } from '@iconify/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

interface UncopiedTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmClose: () => void;
}

export const UncopiedTokenDialog = ({
  open,
  onOpenChange,
  onConfirmClose,
}: UncopiedTokenDialogProps) => {
  const { t } = useTranslation(['integrations']);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay
        className="!mt-0 absolute inset-0 bg-black/30 z-20"
        data-testid="uncopied-token-dialog-overlay"
      />
      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-0 z-30 focus:outline-none"
        role="alertdialog"
        aria-modal="true"
        onEscapeKeyDown={(e) => e.stopPropagation()}
        data-testid="uncopied-token-dialog-content"
      >
        <div
          className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
          data-testid="uncopied-token-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Icon
                icon="mingcute:alert-fill"
                className="inline bg-red-100 rounded-lg text-red-500 p-2 box-content"
              />
              {t('integrations:api_integration.uncopied_token_dialog.title')}
            </AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogDescription>
            {t('integrations:api_integration.uncopied_token_dialog.subtitle')}
          </AlertDialogDescription>

          <Separator className="my-7" />

          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button
                size="md"
                variant="secondary"
                data-testid="uncopied-token-dialog-cancel-button"
              >
                {t('integrations:api_integration.uncopied_token_dialog.button.cancel')}
              </Button>
            </AlertDialogCancel>

            <Button
              variant="danger"
              size="md"
              onClick={onConfirmClose}
              data-testid="uncopied-token-dialog-close-anyway-button"
            >
              {t('integrations:api_integration.uncopied_token_dialog.button.close_anyway')}
            </Button>
          </AlertDialogFooter>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
